import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '../engine/messaging';
import { apiFetch, type ApiTransportDeps } from '../lib/api/http';
import { isBanepadDevicePaired, padTransportDeps } from '../lib/api/pad';

// ─────────────────────────────────────────────────────────────────────
// «KONTAKT SENTERET», FROM THE PAD'S SIDE (#333).
//
// The sheet used `useMessaging`, which reads and writes the club's ENTIRE
// `messages_v1` blob and polls it. That meant a tablet in a corridor held every
// conversation in the club — the last generic-Storage dependency left on the
// physical pad after #185.
//
// This talks to `/pad/messages` instead, which answers ONE conversation: this
// court's. The court is the device record's, so there is no id here for
// anything to be substituted in — this hook cannot ask for another court's
// messages, because there is nothing to ask with.
// ─────────────────────────────────────────────────────────────────────

/** Poll cadence — the sheet asks for the faster one while it is open. */
export const PAD_MESSAGES_POLL_OPEN_MS = 10_000;
export const PAD_MESSAGES_POLL_IDLE_MS = 30_000;

export interface PadConversation {
  conversationId: string | null;
  messages: Message[];
  unread: number;
  disabled: boolean;
  clubSenderId: string;
}

export interface PadMessagingApi extends PadConversation {
  loaded: boolean;
  send: (text: string) => Promise<void>;
  markRead: () => Promise<void>;
  setDisabled: (disabled: boolean) => Promise<void>;
}

const EMPTY: PadConversation = {
  conversationId: null,
  messages: [],
  unread: 0,
  disabled: false,
  clubSenderId: 'club',
};

export function usePadMessaging(opts: { pollMs?: number } = {}): PadMessagingApi {
  const pollMs = opts.pollMs ?? PAD_MESSAGES_POLL_IDLE_MS;
  const [state, setState] = useState<PadConversation>(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  // PAIRED, NOT A TOKEN (#663). The cookie authenticates; this only decides
  // whether there is a pad here to have a conversation at all.
  const pairedRef = useRef<boolean>(isBanepadDevicePaired());
  pairedRef.current = isBanepadDevicePaired();

  /** One request; the response IS the conversation, so every verb reuses it. */
  const call = useCallback(
    async (path: string, init?: RequestInit): Promise<PadConversation | null> => {
      if (!pairedRef.current) return null;
      try {
        const res = await apiFetch(path, init, padTransportDeps() as ApiTransportDeps);
        if (!res.ok) return null;
        const body = (await res.json().catch(() => null)) as PadConversation | null;
        return body;
      } catch {
        // A pad on hall Wi-Fi misses requests. Keeping the last conversation on
        // screen is better than blanking it, and the poll below tries again.
        return null;
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = await call('/pad/messages');
      if (cancelled) return;
      if (next) setState(next);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [call, tick]);

  useEffect(() => {
    const id = setTimeout(() => setTick((n) => n + 1), pollMs);
    return () => clearTimeout(id);
  }, [pollMs, tick]);

  const post = useCallback(
    async (path: string, body?: unknown) => {
      const next = await call(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });
      // The server returns the conversation it just changed, so the sheet shows
      // what the club has rather than what the pad hoped for.
      if (next) setState(next);
    },
    [call],
  );

  return {
    ...state,
    loaded,
    send: useCallback((text: string) => post('/pad/messages', { text }), [post]),
    markRead: useCallback(() => post('/pad/messages/read'), [post]),
    setDisabled: useCallback(
      (disabled: boolean) => post('/pad/messages/disabled', { disabled }),
      [post],
    ),
  };
}
